/* Browns Digital Studio — Apple-style form controls (no native pickers) */
"use strict";

function bdSelectCloseAll(){
  document.querySelectorAll(".bd-select.open").forEach(x => {
    x.classList.remove("open");
    const m = x.querySelector(".bd-select-trigger");
    const menu = x._bdMenuEl || x.querySelector(".bd-select-menu");
    if (m) m.setAttribute("aria-expanded", "false");
    if (menu){
      menu.hidden = true;
      menu.classList.remove("is-open");
      if (x._bdMenuPortaled && menu.parentElement !== x){
        x.appendChild(menu);
        menu.style.position = "";
        menu.style.left = "";
        menu.style.top = "";
        menu.style.width = "";
        menu.style.zIndex = "";
        menu.style.maxHeight = "";
        menu.classList.remove("bd-select-menu--portaled");
      }
      x._bdMenuPortaled = false;
    }
  });
}

function bdSelectClickInside(target){
  return !!(target?.closest?.(".bd-select") || target?.closest?.(".bd-select-menu"));
}

if (!window._bdSelectDocBound){
  window._bdSelectDocBound = true;
  document.addEventListener("click", e => {
    if (bdSelectClickInside(e.target)) return;
    bdSelectCloseAll();
  });
  document.addEventListener("keydown", e => { if (e.key === "Escape") bdSelectCloseAll(); });
}

/* Custom select — replaces native <select> everywhere in Studio */
function bdSelect(opts, val, onChange, cfg = {}){
  const wrap = document.createElement("div");
  wrap.className = "bd-select" + (cfg.className ? " " + cfg.className : "");
  if (cfg.ariaLabel) wrap.setAttribute("aria-label", cfg.ariaLabel);

  const trig = document.createElement("button");
  trig.type = "button";
  trig.className = "bd-select-trigger";
  trig.setAttribute("aria-haspopup", "listbox");
  trig.setAttribute("aria-expanded", "false");

  const menu = document.createElement("div");
  menu.className = "bd-select-menu";
  menu.hidden = true;
  menu.setAttribute("role", "listbox");
  wrap._bdMenuEl = menu;

  function clearPortalStyles(){
    menu.style.position = "";
    menu.style.left = "";
    menu.style.top = "";
    menu.style.width = "";
    menu.style.zIndex = "";
    menu.style.maxHeight = "";
    menu.classList.remove("bd-select-menu--portaled");
  }

  function detachPortal(){
    if (!wrap._bdMenuPortaled) return;
    wrap.appendChild(menu);
    clearPortalStyles();
    wrap._bdMenuPortaled = false;
  }

  function isOpen(){
    return wrap.classList.contains("open") && !menu.hidden;
  }

  function positionPortal(){
    if (!wrap._bdMenuPortaled) return;
    const r = trig.getBoundingClientRect();
    const mh = Math.min(Math.max(menu.scrollHeight || 0, 44), 320);
    const spaceBelow = window.innerHeight - r.bottom - 12;
    const spaceAbove = r.top - 12;
    const openUp = spaceBelow < mh && spaceAbove > spaceBelow;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - Math.max(r.width, 168) - 8));
    menu.style.position = "fixed";
    menu.style.left = left + "px";
    menu.style.width = Math.max(r.width, 168) + "px";
    menu.style.maxHeight = Math.min(320, openUp ? spaceAbove : spaceBelow) + "px";
    menu.style.top = openUp
      ? Math.max(8, r.top - Math.min(mh, spaceAbove) - 6) + "px"
      : (r.bottom + 6) + "px";
    menu.style.zIndex = "10050";
  }

  function attachPortal(){
    if (cfg.portal === false || wrap._bdMenuPortaled) return;
    document.body.appendChild(menu);
    wrap._bdMenuPortaled = true;
    menu.classList.add("bd-select-menu--portaled");
  }

  function closeMenu(){
    menu.hidden = true;
    menu.classList.remove("is-open");
    wrap.classList.remove("open");
    trig.setAttribute("aria-expanded", "false");
    detachPortal();
  }

  function openMenu(){
    document.querySelectorAll(".bd-select.open").forEach(other => {
      if (other === wrap) return;
      other.classList.remove("open");
      const om = other._bdMenuEl || other.querySelector(".bd-select-menu");
      const ot = other.querySelector(".bd-select-trigger");
      if (ot) ot.setAttribute("aria-expanded", "false");
      if (om){
        om.hidden = true;
        om.classList.remove("is-open");
        if (other._bdMenuPortaled && om.parentElement !== other){
          other.appendChild(om);
          om.style.position = "";
          om.style.left = "";
          om.style.top = "";
          om.style.width = "";
          om.style.zIndex = "";
          om.style.maxHeight = "";
          om.classList.remove("bd-select-menu--portaled");
        }
        other._bdMenuPortaled = false;
      }
    });
    menu.hidden = false;
    menu.classList.add("is-open");
    wrap.classList.add("open");
    trig.setAttribute("aria-expanded", "true");
    if (cfg.portal === false){
      clearPortalStyles();
      return;
    }
    /* Portal to body — fixed coords break inside .main / transformed ancestors */
    attachPortal();
    requestAnimationFrame(() => {
      positionPortal();
      requestAnimationFrame(positionPortal);
    });
  }

  const paint = () => {
    const cur = opts.find(([k]) => k === val) || opts[0];
    trig.innerHTML = `<span class="bd-select-val">${esc(cur?.[1] || cfg.placeholder || "—")}</span><i data-lucide="chevron-down" class="lu14 bd-select-chev" aria-hidden="true"></i>`;
    menu.innerHTML = "";
    opts.forEach(entry => {
      const k = entry[0];
      const label = entry[1];
      const desc = entry[2] || "";
      const b = document.createElement("button");
      b.type = "button";
      b.className = "bd-opt" + (k === val ? " on" : "") + (desc ? " has-desc" : "");
      b.setAttribute("role", "option");
      b.setAttribute("aria-selected", k === val ? "true" : "false");
      b.innerHTML = desc
        ? `<span class="bd-opt-main"><strong>${esc(label)}</strong><em>${esc(desc)}</em></span>${k === val ? '<i data-lucide="check" class="lu14" aria-hidden="true"></i>' : ""}`
        : `<span>${esc(label)}</span>${k === val ? '<i data-lucide="check" class="lu14" aria-hidden="true"></i>' : ""}`;
      b.onclick = e => {
        e.stopPropagation();
        if (val !== k){
          val = k;
          onChange(k, true);
        }
        closeMenu();
        paint();
        if (window.lucide) lucide.createIcons();
      };
      menu.appendChild(b);
    });
    if (window.lucide) lucide.createIcons();
    if (!menu.hidden && wrap.classList.contains("open") && wrap._bdMenuPortaled) requestAnimationFrame(positionPortal);
  };

  menu.addEventListener("click", e => e.stopPropagation());
  menu.addEventListener("mousedown", e => e.stopPropagation());

  trig.onclick = e => {
    e.stopPropagation();
    if (isOpen()) closeMenu();
    else openMenu();
  };

  trig.onkeydown = e => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " "){
      e.preventDefault();
      if (!isOpen()) openMenu();
    }
    if (e.key === "Escape" && isOpen()){
      e.preventDefault();
      closeMenu();
    }
  };

  wrap.getValue = () => val;
  wrap.setValue = v => { val = v; paint(); };

  wrap.appendChild(trig);
  wrap.appendChild(menu);
  paint();
  return wrap;
}

/* Custom colour picker — 2D canvas, shade strip, swatches, hex field */
const BD_COLOR_SWATCHES = ["#355cff","#0071e3","#1d1d1f","#30d158","#ff9f0a","#ff375f","#bf5af2","#ffffff","#86868b","#000000"];

function bdColorPicker(val, onChange, onCommit, opts = {}){
  const popover = !!opts.popover;
  val = /^#[0-9a-fA-F]{6}$/.test(val || "") ? val : "#355cff";

  // ── state ──
  let hsvH = 0, hsvS = 1, hsvV = 1;
  function hexToHsv(hex){
    let r=parseInt(hex.slice(1,3),16)/255, g=parseInt(hex.slice(3,5),16)/255, b=parseInt(hex.slice(5,7),16)/255;
    const max=Math.max(r,g,b), min=Math.min(r,g,b), d=max-min;
    let h=0;
    if(d){ h = max===r?((g-b)/d+6)%6 : max===g?(b-r)/d+2 : (r-g)/d+4; h*=60; }
    return [h, max?d/max:0, max];
  }
  function hsvToHex(h,s,v){
    h = ((h % 360) + 360) % 360;
    const c=v*s, x=c*(1-Math.abs((h/60)%2-1)), m=v-c;
    let r=0,g=0,b=0;
    if(h<60){r=c;g=x;}else if(h<120){r=x;g=c;}else if(h<180){g=c;b=x;}
    else if(h<240){g=x;b=c;}else if(h<300){r=x;b=c;}else{r=c;b=x;}
    const t=n=>Math.round((n+m)*255).toString(16).padStart(2,"0");
    return "#"+t(r)+t(g)+t(b);
  }
  const hexDigits = h => String(h || "").replace(/^#/, "").toUpperCase();
  [hsvH,hsvS,hsvV] = hexToHsv(val);
  let mapX = null, mapY = null;

  // ── DOM ──
  const wrap = document.createElement("div");
  wrap.className = "bd-color";

  const preview = document.createElement("button");
  preview.type = "button";
  preview.className = "bd-color-preview";
  preview.style.background = val;
  preview.title = "Open colour picker";
  preview.setAttribute("aria-label", "Open colour picker");

  const text = document.createElement("input");
  text.className = "bd-color-text";
  text.value = val.toUpperCase();
  text.spellcheck = false;
  text.maxLength = 7;
  text.autocomplete = "off";
  text.inputMode = "text";
  text.placeholder = "#355CFF";
  text.setAttribute("aria-label", "Hex colour");

  const pop = document.createElement("div");
  pop.className = "bd-color-pop";
  pop.hidden = true;

  const compact = !!opts.compact;
  const canvasWrap = document.createElement("div");
  canvasWrap.className = "bd-color-canvas-wrap";
  const canvas = document.createElement("canvas");
  canvas.className = "bd-color-canvas";
  /* Display size drives bitmap size — fixed attrs desync from CSS width:100%. */
  canvas.width = compact ? 280 : 220;
  canvas.height = compact ? 96 : 130;
  canvasWrap.appendChild(canvas);
  const dot = document.createElement("div");
  dot.className = "bd-color-dot";
  canvasWrap.appendChild(dot);

  // Shade strip — light → pure → dark of the current colour
  const shadeWrap = document.createElement("div");
  shadeWrap.className = "bd-color-hue-wrap bd-color-shade-wrap";
  const shadeTrack = document.createElement("div");
  shadeTrack.className = "bd-color-hue-track bd-color-shade-track";
  shadeTrack.setAttribute("aria-hidden", "true");
  const shadeRange = document.createElement("input");
  shadeRange.type = "range"; shadeRange.min = "0"; shadeRange.max = "100"; shadeRange.step = "1";
  shadeRange.className = "bd-color-hue bd-color-shade";
  shadeRange.setAttribute("aria-label", "Shade");
  shadeRange.value = String(Math.round((1 - hsvV) * 100));
  shadeWrap.appendChild(shadeTrack);
  shadeWrap.appendChild(shadeRange);

  // Swatches
  const sw = document.createElement("div");
  sw.className = "bd-color-swatches";
  const showSwatches = opts.swatches !== false;

  // Hex — live tip + soft # prefix + digits (no nested chip)
  const hexRow = document.createElement("div");
  hexRow.className = "bd-color-hex-row";
  const hexLabel = document.createElement("span");
  hexLabel.className = "bd-color-hex-label";
  hexLabel.textContent = "HEX";
  const hexField = document.createElement("div");
  hexField.className = "bd-color-hex-field";
  const hexSwatch = document.createElement("span");
  hexSwatch.className = "bd-color-hex-swatch";
  hexSwatch.setAttribute("aria-hidden", "true");
  hexSwatch.style.background = val;
  const hexHash = document.createElement("span");
  hexHash.className = "bd-color-hex-hash";
  hexHash.textContent = "#";
  const hex = document.createElement("input");
  hex.className = "bd-color-hex";
  hex.value = hexDigits(val);
  hex.spellcheck = false;
  hex.maxLength = 6;
  hex.autocomplete = "off";
  hex.inputMode = "text";
  hex.placeholder = "355CFF";
  hex.setAttribute("aria-label", "Hex colour without hash");
  hexField.appendChild(hexSwatch);
  hexField.appendChild(hexHash);
  hexField.appendChild(hex);
  hexRow.appendChild(hexLabel);
  hexRow.appendChild(hexField);

  function updateShadeTrack(){
    const mid = hsvToHex(hsvH, Math.max(0.55, hsvS), 1);
    shadeTrack.style.background = `linear-gradient(to right, #ffffff 0%, ${mid} 55%, #000000 100%)`;
  }

  function syncCanvasBitmap(){
    const r = canvasWrap.getBoundingClientRect();
    const w = Math.max(1, Math.round(r.width));
    const h = Math.max(1, Math.round(r.height));
    if (w < 2 || h < 2) return false;
    if (canvas.width !== w || canvas.height !== h){
      canvas.width = w;
      canvas.height = h;
      return true;
    }
    return false;
  }

  function mapPos(){
    const hue = ((hsvH % 360) + 360) % 360;
    return [
      mapX != null ? mapX : (hue / 360),
      mapY != null ? mapY : (1 - hsvS)
    ];
  }

  function drawCanvas(){
    syncCanvasBitmap();
    const ctx = canvas.getContext("2d");
    const w = canvas.width, h = canvas.height;
    /* Hue left→right, saturation top→bottom (at full brightness). */
    for (let x = 0; x < w; x++){
      ctx.fillStyle = `hsl(${(x / Math.max(1, w - 1)) * 360},100%,50%)`;
      ctx.fillRect(x, 0, 1, h);
    }
    const wg = ctx.createLinearGradient(0, 0, 0, h);
    wg.addColorStop(0, "rgba(255,255,255,0)");
    wg.addColorStop(1, "rgba(255,255,255,1)");
    ctx.fillStyle = wg;
    ctx.fillRect(0, 0, w, h);
    const [mx, my] = mapPos();
    dot.style.left = (mx * 100) + "%";
    dot.style.top = (my * 100) + "%";
    updateShadeTrack();
  }

  function paintSwatches(){
    sw.innerHTML = "";
    BD_COLOR_SWATCHES.forEach(c => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "bd-color-swatch";
      b.dataset.hex = c.toLowerCase();
      b.style.background = c;
      b.title = c;
      b.onclick = e => { e.stopPropagation(); setHex(c, true); };
      sw.appendChild(b);
    });
    syncSwatchSelection();
  }

  function syncSwatchSelection(){
    const cur = String(val || "").toLowerCase();
    sw.querySelectorAll(".bd-color-swatch").forEach(b => {
      b.classList.toggle("on", b.dataset.hex === cur);
    });
  }

  function applyHexDigits(raw, commit){
    const v = String(raw || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();
    hex.value = v;
    if (v.length !== 6) return false;
    setHex("#" + v, commit);
    return true;
  }

  function setFromHsv(commit){
    val = hsvToHex(hsvH, hsvS, hsvV);
    preview.style.background = val;
    if (hexSwatch) hexSwatch.style.background = val;
    if (document.activeElement !== text) text.value = val.toUpperCase();
    if (document.activeElement !== hex) hex.value = hexDigits(val);
    shadeRange.value = String(Math.round((1 - hsvV) * 100));
    syncSwatchSelection();
    drawCanvas();
    onChange(val);
    if (commit && onCommit) onCommit(val);
  }

  function setHex(c, commit){
    if (!/^#[0-9a-fA-F]{6}$/i.test(c)) return;
    val = "#" + c.slice(1).toUpperCase();
    [hsvH,hsvS,hsvV] = hexToHsv(val);
    mapX = mapY = null;
    setFromHsv(commit);
  }

  function canvasCoord(e){
    const r = canvasWrap.getBoundingClientRect();
    const pt = e.touches?.[0] || e.changedTouches?.[0];
    const clientX = e.clientX ?? pt?.clientX ?? 0;
    const clientY = e.clientY ?? pt?.clientY ?? 0;
    const nx = r.width ? (clientX - r.left) / r.width : 0;
    const ny = r.height ? (clientY - r.top) / r.height : 0;
    return [
      Math.max(0, Math.min(1, nx)),
      Math.max(0, Math.min(1, ny))
    ];
  }
  let dragging = false;
  function canvasPick(e, commit){
    const [nx, ny] = canvasCoord(e);
    mapX = nx;
    mapY = ny;
    hsvH = nx >= 1 ? 359.999 : nx * 360;
    hsvS = 1 - ny;
    setFromHsv(commit);
  }
  const onCanvasDown = e => {
    e.preventDefault();
    e.stopPropagation();
    dragging = true;
    canvasWrap.setPointerCapture?.(e.pointerId);
    canvasPick(e, false);
  };
  const onCanvasMove = e => { if (dragging) canvasPick(e, false); };
  const onCanvasUp = e => {
    if (!dragging) return;
    dragging = false;
    try { canvasWrap.releasePointerCapture?.(e.pointerId); } catch {}
    canvasPick(e, true);
  };
  canvasWrap.addEventListener("pointerdown", onCanvasDown);
  canvasWrap.addEventListener("pointermove", onCanvasMove);
  canvasWrap.addEventListener("pointerup", onCanvasUp);
  canvasWrap.addEventListener("pointercancel", () => { dragging = false; });
  document.addEventListener("pointermove", onCanvasMove);
  document.addEventListener("pointerup", onCanvasUp);
  if (typeof ResizeObserver !== "undefined"){
    const ro = new ResizeObserver(() => { if (!pop.hidden) drawCanvas(); });
    ro.observe(canvasWrap);
  }

  /* Track is light→dark left→right, so slider 0 = light (V=1), 100 = dark (V=0). */
  shadeRange.addEventListener("pointerdown", e => e.stopPropagation());
  shadeRange.addEventListener("click", e => e.stopPropagation());
  shadeRange.addEventListener("input",  () => { hsvV = 1 - ((+shadeRange.value) / 100); setFromHsv(false); });
  shadeRange.addEventListener("change", () => { hsvV = 1 - ((+shadeRange.value) / 100); setFromHsv(true); });

  hex.addEventListener("pointerdown", e => e.stopPropagation());
  hex.addEventListener("click", e => e.stopPropagation());
  hex.addEventListener("keydown", e => {
    e.stopPropagation();
    if (e.key === "Enter"){
      applyHexDigits(hex.value, true);
      hex.blur();
    }
  });
  hex.addEventListener("blur", () => {
    if (!applyHexDigits(hex.value, true)) hex.value = hexDigits(val);
  });
  hex.addEventListener("input", () => { applyHexDigits(hex.value, false); });
  hex.addEventListener("paste", e => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text") || "";
    applyHexDigits(pasted.replace(/^#/, ""), false);
  });

  const applyTriggerHex = (raw, commit) => {
    let v = String(raw || "").trim();
    if (!v) return false;
    if (v[0] !== "#") v = "#" + v;
    v = "#" + v.slice(1).replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    text.value = v.toUpperCase();
    if (v.length !== 7) return false;
    setHex(v, commit);
    return true;
  };
  text.addEventListener("pointerdown", e => e.stopPropagation());
  text.addEventListener("click", e => e.stopPropagation());
  text.addEventListener("keydown", e => {
    e.stopPropagation();
    if (e.key === "Enter"){
      applyTriggerHex(text.value, true);
      text.blur();
    }
  });
  text.addEventListener("blur", () => {
    if (!applyTriggerHex(text.value, true)) text.value = val.toUpperCase();
  });
  text.addEventListener("input", () => { applyTriggerHex(text.value, false); });
  text.addEventListener("paste", e => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData("text") || "";
    applyTriggerHex(pasted, false);
  });

  pop.appendChild(canvasWrap);
  pop.appendChild(shadeWrap);
  if (showSwatches) pop.appendChild(sw);
  /* Trigger already has an editable hex field — keep the pop compact.
     Inline popover mode always shows HEX so the value stays editable. */
  if (popover) pop.appendChild(hexRow);

  const portal = !!opts.portal && !popover;
  const estimatePopHeight = () => {
    const canvasH = compact ? 96 : 130;
    /* canvas + shade + swatches + padding — no hex row in trigger mode */
    return canvasH + 20 + 36 + 24 + (popover ? 48 : 0);
  };
  const placePop = () => {
    if (!portal || pop.hidden) return;
    const r = preview.getBoundingClientRect();
    const pad = 12;
    const gap = 8;
    const width = Math.min(268, window.innerWidth - pad * 2);
    const measured = pop.getBoundingClientRect().height;
    const popH = measured > 40 ? measured : estimatePopHeight();
    const spaceBelow = window.innerHeight - r.bottom - pad;
    const spaceAbove = r.top - pad;
    const need = popH + gap;
    let openUp;
    if (opts.preferUp === true) {
      openUp = spaceAbove >= Math.min(need, 180) || spaceAbove >= spaceBelow;
    } else if (opts.preferUp === false) {
      openUp = spaceBelow < need && spaceAbove > spaceBelow;
    } else {
      /* auto: prefer the side with enough room; bias upward near viewport bottom */
      openUp = (spaceBelow < need && spaceAbove >= Math.min(need, 160))
        || (spaceBelow < 200 && spaceAbove > spaceBelow);
    }

    let left = r.left;
    if (left + width > window.innerWidth - pad) left = window.innerWidth - pad - width;
    if (left < pad) left = pad;

    const maxH = Math.max(148, (openUp ? spaceAbove : spaceBelow) - gap);
    pop.style.position = "fixed";
    pop.style.left = left + "px";
    pop.style.width = width + "px";
    pop.style.zIndex = "14000";
    pop.style.maxHeight = maxH + "px";
    pop.style.overflowX = "hidden";
    pop.style.overflowY = maxH < popH - 4 ? "auto" : "visible";
    if (openUp) {
      pop.style.top = "auto";
      pop.style.bottom = (window.innerHeight - r.top + gap) + "px";
    } else {
      pop.style.bottom = "auto";
      pop.style.top = (r.bottom + gap) + "px";
    }
  };
  const closePop = () => {
    pop.hidden = true;
    pop.style.maxHeight = "";
    pop.style.overflowY = "";
  };
  const openPop = () => {
    document.querySelectorAll(".bd-color-pop").forEach(p => { if (p !== pop) p.hidden = true; });
    if (portal && pop.parentNode !== document.body) document.body.appendChild(pop);
    pop.hidden = false;
    placePop();
    requestAnimationFrame(() => {
      drawCanvas();
      placePop();
    });
    syncSwatchSelection();
    hex.value = hexDigits(val);
  };

  pop.addEventListener("pointerdown", e => e.stopPropagation());
  pop.addEventListener("click", e => e.stopPropagation());

  preview.onclick = e => {
    e.stopPropagation();
    if (pop.hidden) openPop();
    else closePop();
  };
  if (!popover){
    document.addEventListener("pointerdown", e => {
      if (wrap.contains(e.target) || pop.contains(e.target)) return;
      closePop();
    });
    if (portal){
      window.addEventListener("resize", placePop);
      window.addEventListener("scroll", placePop, true);
    }
  }

  if (!popover){
    wrap.appendChild(preview);
    wrap.appendChild(text);
    if (!portal) wrap.appendChild(pop);
  } else {
    wrap.className = "bd-color bd-color--popover" + (compact ? " bd-color--compact" : "");
    pop.hidden = false;
    pop.style.position = "static";
    wrap.appendChild(pop);
  }

  wrap._bdSetColor = (c, commit = true) => setHex(c, commit);
  wrap._bdCloseColor = closePop;

  drawCanvas();
  if (showSwatches) paintSwatches();
  return wrap;
}

function bdColorPopover(val, onChange, onCommit){
  return bdColorPicker(val, onChange, onCommit, { popover: true });
}

function hexToHue(hex){
  const r=parseInt(hex.slice(1,3),16)/255,g=parseInt(hex.slice(3,5),16)/255,b=parseInt(hex.slice(5,7),16)/255;
  const max=Math.max(r,g,b),min=Math.min(r,g,b),d=max-min;
  if(!d) return 0;
  let h=max===r?((g-b)/d)%6:max===g?(b-r)/d+2:(r-g)/d+4;
  return Math.round((h*60+360)%360);
}
function hueToHex(h,base){
  const s=0.72,l=0.48;
  const c=(1-Math.abs(2*l-1))*s;
  const x=c*(1-Math.abs((h/60)%2-1));
  const m=l-c/2;
  let r=0,g=0,b=0;
  if(h<60){r=c;g=x;}else if(h<120){r=x;g=c;}else if(h<180){g=c;b=x;}
  else if(h<240){g=x;b=c;}else if(h<300){r=x;b=c;}else{r=c;b=x;}
  const to=n=>Math.round((n+m)*255).toString(16).padStart(2,"0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
function rgbToHex(rgb){
  if(rgb.startsWith("#")) return rgb.toLowerCase();
  const m=rgb.match(/\d+/g);
  if(!m||m.length<3) return "";
  return "#"+m.slice(0,3).map(n=>(+n).toString(16).padStart(2,"0")).join("");
}
